import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import Button from '../Button';
import { formatDate } from '../../utils/dateUtils';
import { endorserDisplayName } from '../../constants/endorsements';
import { ballotDesignation } from '../ballot-initiatives/initiativeDisplay';
import {
  DETAIL_LINK,
  DetailCard,
  ExternalTextLink,
  MUTED_TEXT,
} from '../ballot-initiatives/detail/detailAtoms';
import { PartyDot, StatusPill } from './PersonAtoms';
import { candidateOffice, generalElectionDate, outcomeLabel } from './personDisplay';
import type { CandidateData, CandidateMeasurePosition, CandidateStatement } from './types';
import { RESPONSE_STATUS_LABELS, firstParagraph, groupStatements, needsExpander } from '../../citizen/domain/candidateProfiles';

const INITIAL_VISIBLE = 3;

/**
 * The race: the other candidates for the same office, incumbent first.
 * While it's undecided only ACTIVE candidates render (QA, 2026-08-08) — the API
 * already excludes the viewed person and `inactive`; this narrows out withdrawn /
 * suspended too. Once it's settled (ELECTION_OUTCOMES_PRD §5.13) every candidate
 * shows with how they finished, so a winner never reads as unopposed.
 */
const SETTLED_SHOWN = new Set(['active', 'elected', 'defeated']);

const CandidateRace: React.FC<{ candidate: CandidateData; isPublic?: boolean }> = ({ candidate, isPublic = false }) => {
  const [showAll, setShowAll] = useState(false);
  const settled = Boolean(candidate.settled);
  const others = (candidate.opponents || []).filter((o) => {
    const status = (o.status || '').toLowerCase();
    return settled ? SETTLED_SHOWN.has(status) : status === 'active';
  });
  const visible = showAll ? others : others.slice(0, INITIAL_VISIBLE);

  const cycleType = candidate.election_cycle?.election_type?.toLowerCase() ?? null;
  const general = generalElectionDate(candidate);
  const subLine = [
    candidateOffice(candidate),
    cycleType === 'primary' ? 'Primary' : 'General election',
    general ? formatDate(general, { month: 'long' }) : null,
    settled ? `Result: ${outcomeLabel(candidate.status)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <DetailCard
      id="person-race"
      title="The race"
      subLine={subLine}
      aside={<span className={MUTED_TEXT}>{others.length} other candidate{others.length === 1 ? '' : 's'}</span>}
    >
      {others.length > 0 ? (
        <>
          <ul className="space-y-2">
            {visible.map((o, i) => (
              <li
                key={o.person_id ?? i}
                className="flex items-center justify-between gap-3 rounded-[10px] border border-border-light bg-background-secondary px-3.5 py-3 dark:border-border-dark dark:bg-background-dark-tertiary"
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <span className="mt-2 shrink-0"><PartyDot party={o.party} /></span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {o.person_id ? (
                        <Link
                          to={isPublic ? `/public/candidates/${o.person_id}` : `/candidates/${o.person_id}`}
                          className={`text-[15px] font-semibold ${DETAIL_LINK}`}
                        >
                          {o.name}
                        </Link>
                      ) : (
                        <span className="text-[15px] font-semibold text-text-primary dark:text-text-dark-primary">{o.name}</span>
                      )}
                      {o.incumbent && (
                        <span className="rounded bg-warning-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">
                          Incumbent
                        </span>
                      )}
                    </div>
                    <div className={`text-[13px] ${MUTED_TEXT}`}>{o.party}</div>
                  </div>
                </div>
                <StatusPill status={settled ? outcomeLabel(o.status) : (o.status || '').toLowerCase()} />
              </li>
            ))}
          </ul>
          {others.length > INITIAL_VISIBLE && (
            <Button
              variant="ghost"
              size="sm"
              noShadow
              fullWidth
              className="mt-3"
              icon={showAll ? ChevronUp : ChevronDown}
              iconPosition="right"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Show fewer candidates' : `Show all ${others.length} candidates`}
            </Button>
          )}
        </>
      ) : (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {settled ? 'No other candidates in this race.' : 'No other active candidates in this race.'}
        </p>
      )}
    </DetailCard>
  );
};

const STANCE: Record<string, { label: string; className: string }> = {
  support: { label: 'Support', className: 'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-300' },
  oppose: { label: 'Oppose', className: 'bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-300' },
  unclear: { label: 'No clear position', className: 'bg-background-tertiary text-slate-500 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary' },
  declined: { label: 'Declined to say', className: 'bg-background-tertiary text-slate-500 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary' },
  no_position: { label: 'No position found', className: 'bg-background-tertiary text-slate-500 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary' },
};

/** The measure's short name for a list row: its popular name, else the ballot question (clamped to one line). */
function measureShortTitle(p: CandidateMeasurePosition): string | null {
  return p.popular_name || p.ballot_title || null;
}

function StancePill({ stance }: { stance: string }) {
  const s = STANCE[stance] ?? STANCE.no_position;
  return <span className={`inline-flex shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.className}`}>{s.label}</span>;
}

/**
 * The candidate's stances on this cycle's ballot measures (CANDIDATE_PROFILES_PRD.md
 * §5.5.5) — the same rows the measure page reads, seen from the candidate's side.
 * Published profiles only; the API sends none otherwise.
 *
 * A compact list — designation, one-line name, stance — so a ballot of long
 * questions stays scannable; a row opens the detail (full ballot question, the
 * candidate's words, the source and its date, a link to the measure) in a dialog.
 */
export const MeasurePositions: React.FC<{ positions: CandidateMeasurePosition[]; isPublic?: boolean }> = ({
  positions,
  isPublic = false,
}) => {
  const [openId, setOpenId] = useState<string | null>(null);
  if (positions.length === 0) return null;
  const sorted = [...positions].sort((a, b) => (a.ballot_order ?? 999) - (b.ballot_order ?? 999));
  const open = sorted.find((p) => p.id === openId) ?? null;
  return (
    <DetailCard
      id="person-measure-positions"
      title="Positions on ballot measures"
      subLine="The candidate's own stated words or recorded referral vote, with the source. Select a measure for details."
    >
      <ul>
        {sorted.map((p) => {
          const designation = ballotDesignation(p.measure_number);
          const title = measureShortTitle(p);
          return (
            <li key={p.id} className="border-b border-border-light last:border-b-0 dark:border-border-dark">
              <button
                type="button"
                onClick={() => setOpenId(p.id)}
                aria-haspopup="dialog"
                className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-background-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-background-dark-tertiary"
              >
                {/* Phones stack designation, name and stance; wider screens put the stance beside the text. */}
                <span className="flex min-w-0 flex-1 flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                  <span className="min-w-0 self-stretch sm:flex-1 sm:self-auto">
                    <span className={`block text-sm font-semibold ${DETAIL_LINK}`}>{designation || title || 'Ballot measure'}</span>
                    {designation && title && (
                      <span className="line-clamp-2 text-[13px] text-text-secondary sm:line-clamp-1 dark:text-text-dark-secondary" title={title}>{title}</span>
                    )}
                  </span>
                  <StancePill stance={p.stance} />
                </span>
                <ChevronRight className={`h-4 w-4 shrink-0 ${MUTED_TEXT}`} aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
      {open && <MeasurePositionDialog position={open} isPublic={isPublic} onClose={() => setOpenId(null)} />}
    </DetailCard>
  );
};

/** One stance in full: the ballot question, the candidate's words or vote, the source, the measure link. */
function MeasurePositionDialog({ position: p, isPublic, onClose }: { position: CandidateMeasurePosition; isPublic: boolean; onClose: () => void }) {
  const designation = ballotDesignation(p.measure_number);
  const heading = [designation, p.popular_name].filter(Boolean).join(' · ') || p.ballot_title || 'Ballot measure';
  const measureHref = isPublic ? `/public/ballot-initiatives/${p.ballot_initiative_id}` : `/ballot-initiatives/${p.ballot_initiative_id}`;
  const label = 'text-xs font-bold uppercase tracking-[0.12em] text-text-tertiary dark:text-text-dark-tertiary';
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={heading}
      size="md"
      footer={
        <div className="flex w-full justify-end">
          <Link to={measureHref} onClick={onClose} className={`inline-flex items-center gap-1 text-sm font-semibold ${DETAIL_LINK}`}>
            Open the measure page <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      }
    >
      <div className="space-y-4">
        {p.ballot_title && (
          <div>
            <div className={label}>Ballot question</div>
            <p className="mt-1 text-sm leading-relaxed text-text-primary dark:text-text-dark-primary">{p.ballot_title}</p>
          </div>
        )}
        <div>
          <div className={label}>Candidate&apos;s position</div>
          <div className="mt-1.5"><StancePill stance={p.stance} /></div>
          {p.quote && (
            <blockquote className="mt-2 border-l-2 border-border-light pl-3 text-sm italic text-text-secondary dark:border-border-dark dark:text-text-dark-secondary">
              &ldquo;{p.quote}&rdquo;
            </blockquote>
          )}
          {p.stance === 'no_position' && (
            <p className={`mt-2 text-sm ${MUTED_TEXT}`}>
              No public statement or recorded vote was found{p.source_date ? ` as of ${formatDate(p.source_date)}` : ''}.
            </p>
          )}
        </div>
        {(p.source_name || p.source_date) && (
          <div>
            <div className={label}>Source</div>
            <p className="mt-1 text-sm text-text-primary dark:text-text-dark-primary">
              {p.source_name && (p.source_url ? <ExternalTextLink href={p.source_url}>{p.source_name}</ExternalTextLink> : p.source_name)}
              {p.source_name && p.source_date ? ' · ' : null}
              {p.source_date && <span className={MUTED_TEXT}>{formatDate(p.source_date)}</span>}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** One verbatim answer, whole, one click down past 120 words (never trimmed — CANDIDATE_PROFILES_PRD Rule 6). */
function VerbatimAnswer({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = needsExpander(text);
  return (
    <div>
      <blockquote className="mt-1 whitespace-pre-line border-l-2 border-border-light pl-3 text-sm leading-relaxed text-text-secondary dark:border-border-dark dark:text-text-dark-secondary">
        {long && !open ? firstParagraph(text) : text}
      </blockquote>
      {long && (
        <Button variant="ghost" size="sm" noShadow className="mt-1 -ml-2" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? 'Show less' : 'Read the full answer'}
        </Button>
      )}
    </div>
  );
}

/**
 * "In their own words": the candidate's questionnaire answers, quoted word for word
 * and grouped by source (CANDIDATE_PROFILES_PRD §5.5.1) — the same rows the citizen
 * page shows. The API sends them with a published profile only.
 */
export const OwnWords: React.FC<{ statements: CandidateStatement[] }> = ({ statements }) => {
  if (statements.length === 0) return null;
  const groups = groupStatements(statements);
  return (
    <DetailCard
      id="person-own-words"
      title="In their own words"
      subLine={`${statements.length} question${statements.length === 1 ? '' : 's'} from ${groups.length} source${groups.length === 1 ? '' : 's'}, quoted word for word.`}
    >
      <div className="space-y-3">
        {groups.map((g, gi) => (
          <details key={`${g.source_name}|${g.source_date ?? ''}`} open={gi === 0} className="group">
            <summary className="cursor-pointer py-1 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              {g.source_url ? <ExternalTextLink href={g.source_url}>{g.source_name}</ExternalTextLink> : g.source_name}
              {g.source_date ? <span className={`font-normal ${MUTED_TEXT}`}> · {formatDate(g.source_date)}</span> : null}
            </summary>
            <ul className="mt-2 space-y-4">
              {g.rows.map((row) => (
                <li key={row.id}>
                  {row.question && <p className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">{row.question}</p>}
                  {row.response_status === 'answered' && row.body ? (
                    <VerbatimAnswer text={row.body} />
                  ) : (
                    <p className={`mt-1 text-sm italic ${MUTED_TEXT}`}>
                      {RESPONSE_STATUS_LABELS[row.response_status]}{row.source_date ? ` as of ${formatDate(row.source_date)}` : ''}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </DetailCard>
  );
};

/** Sourced endorsements from the profile tables (CANDIDATE_PROFILES_PRD.md §5.5.1). */
export const Endorsements: React.FC<{ candidate: CandidateData }> = ({ candidate }) => {
  const endorsements = candidate.endorsements ?? [];
  if (endorsements.length === 0) return null;
  return (
    <DetailCard id="person-endorsements" title="Endorsements">
      <ul>
        {endorsements.map((e) => (
          <li
            key={e.id}
            // Wraps instead of squeezing: the name keeps its own width (up to the
            // full row) and a long source drops to the next line, so zoom or a
            // narrow card never crushes the name into one letter per line.
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-t border-border-light py-2.5 first:border-t-0 first:pt-0 dark:border-border-dark"
          >
            <span className="max-w-full break-words text-sm font-medium text-text-primary dark:text-text-dark-primary">{endorserDisplayName(e)}</span>
            {/* The shared endorsement fields (ENDORSEMENTS_PRD §5.7): the named source, linked, then the date. */}
            <span className={`min-w-0 max-w-full break-words text-[13px] ${MUTED_TEXT}`}>
              {e.source_url ? (
                // `!inline`: a long source wraps as text, its icon and the date follow it.
                <ExternalTextLink href={e.source_url} className="!inline [&>svg]:ml-1 [&>svg]:inline [&>svg]:align-[-2px]">{e.source_name || 'Source'}</ExternalTextLink>
              ) : e.source_name}
              {(e.source_url || e.source_name) && e.endorsed_on ? ' · ' : null}
              {e.endorsed_on ? formatDate(e.endorsed_on) : null}
            </span>
          </li>
        ))}
      </ul>
    </DetailCard>
  );
};

export default CandidateRace;
